import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TrackByFunction,
  ViewChild,
} from '@angular/core';
import { GoogleMap, MapInfoWindow } from '@angular/google-maps';
import { Subscription } from 'rxjs';
import { changeDeviceView, safelyDetectChanges, unsubscribeAll } from 'src/app/common/app.helpers';
import { MapGotoBlockForm, MapState, ZoomOptions } from 'src/app/interfaces/map.interface';
import { CustomMarker } from 'src/app/models/custom-marker.model';
import { MapFlags } from 'src/app/models/map-flags.model';
import { MapMarkService } from '../map-marker.service';
import { isInAggregationZoomRange, isTransitioningToIndividualsZoomLevel } from '../map.helpers';
import { MapLibraries, MapsAPILoader } from '../map.service';
import MapSettings from '../map.settings';
import { MarkerInfoFactory } from '../marker-info/marker-info.factory';
import { PinItemBindingMediator } from '../markers-handler-helpers/mediators/pin-item-binding.mediator';
import { MapInitialLocationProvider } from './map-initial-location.provider';
import { DEFAULT_CIRCLE_OPTIONS, DEFAULT_MAP_OPTIONS, DEFAULT_MARKER_OPTIONS } from './map-options';
import AppValues from '../../../common/app.values';

@Component({
  selector: 'app-map-container',
  templateUrl: './map-container.component.html',
  styleUrls: ['./map-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MapContainerComponent implements OnInit, OnChanges, OnDestroy {
  @Input({ required: true }) public flags: MapFlags;
  @Input({ required: true }) public markers: CustomMarker[];
  @Input({ required: true }) public mapState: MapState;
  @Input({ required: true }) public zoomOptions: ZoomOptions;
  @Input({ required: true }) public mediator: PinItemBindingMediator;
  @Input({ required: true }) public mapMarker: MapGotoBlockForm;
  @Input({ required: true }) public isAdminSession: boolean;
  @Input({ required: true }) public initialLocationProvider: MapInitialLocationProvider;

  @Output() public mapChanged: EventEmitter<MapState> = new EventEmitter<MapState>();

  public readonly trackByMarkerId: TrackByFunction<CustomMarker> = (_index, marker) => marker.id;

  public mapOptions: google.maps.MapOptions | null = null;
  public markerOptions: google.maps.MarkerOptions | null = null;
  public circleOptions: google.maps.CircleOptions | null = null;
  public popupOptions: google.maps.InfoWindowOptions | null = null;

  public readonly isMapLoaded: Promise<boolean> = inject(MapsAPILoader).load(MapLibraries.MAPS);

  public focusedCircle: CustomMarker | null = null;
  public zoom: number;

  private subscriptions: Subscription[] = [];

  @ViewChild(GoogleMap, { static: false }) private map: GoogleMap;
  @ViewChild(MapInfoWindow) private popup: MapInfoWindow;
  @ViewChild(MarkerInfoFactory, { static: false }) private popupContent: MarkerInfoFactory;

  protected changeDetector = inject(ChangeDetectorRef);
  private mapMarkService = inject(MapMarkService);

  public async ngOnInit(): Promise<void> {
    await this.initMap();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['markers']) {
      this.processClickedMarkerId();
    }

    if (changes['zoomOptions']) {
      this.updateOptions();
    }

    if (this.mapState.zoom !== this.zoom) {
      this.onMapClick();
    }

    this.zoom = this.mapState.zoom;
    this.flags.isInAggregationZoomRange = isInAggregationZoomRange(this.zoom);
    this.updatePopupOptions();
  }

  private updatePopupOptions() {
    if (this.popupOptions) {
      this.popupOptions = {
        ...this.popupOptions,
        headerDisabled: this.flags.isInAggregationZoomRange && this.flags.isNotCovidTab,
      };
    }
  }

  public ngOnDestroy(): void {
    unsubscribeAll(this.subscriptions);
  }

  public subscribeToCityMarkChanges(): void {
    this.subscriptions.push(
      this.mapMarkService.clearMapMarkChanges.subscribe(() => {
        this.mapMarker = null;
        safelyDetectChanges(this.changeDetector);
      }),
    );
  }
  /**
   * @description Called on map's mapClick event and resets the focusedCircle object value.
   */
  public onMapClick(): void {
    this.focusedCircle = null;

    this.closePopup();
  }

  public closePopup(): void {
    if (this.popup) {
      this.popup.close();
      this.mediator.onClearMarkerClick();
    }
  }

  public onMarkerClick(marker: CustomMarker): void {
    const isMobile = window.innerWidth < AppValues.DEFAULT_WIDTH_FOR_MOBILE;
    if (isMobile) {
      this.openMarkerPopup(marker);
    } else {
      this.handleMarkerClick(marker);
    }
  }

  public handleMarkerClick(marker: CustomMarker): void {
    this.mapState.location.center = { lat: marker.latitude, lon: marker.longitude };

    if (marker.isAggregated && this.map.getZoom() < this.mapOptions.maxZoom) {
      if (this.flags.disableMarkerClick) {
        return;
      }

      if (isTransitioningToIndividualsZoomLevel(this.zoom)) {
        this.flags.markerLocationLiteral = marker.locationLiteral;
      }

      if (marker.label && isInAggregationZoomRange(this.zoom) && marker.label.text === '1') {
        this.flags.markerLocationLiteral = marker.locationLiteral;
        this.zoom = MapSettings.MAP_SEARCH_INDIVIDUAL_ZOOM_MIN_LEVEL;
      } else {
        this.zoom++;
      }

      this.closePopup();
      this.flags.disableMarkerClick = true;
      return;
    }

    this.openMarkerPopup(marker);
  }

  public onCircleMouseOver(marker: CustomMarker): void {
    this.focusedCircle = { ...marker };

    this.popup.position = new google.maps.LatLng(marker.latitude, marker.longitude);
    this.popup.open();

    marker.isOpen = true;
  }

  public onMarkerMouseOver(marker: CustomMarker): void {
    const isMobileView = changeDeviceView(AppValues.DEFAULT_WIDTH_FOR_MOBILE);
    if (!isInAggregationZoomRange(this.mapState.zoom) || isMobileView) {
      return;
    }

    this.openMarkerPopup(marker);
  }
  /**
   * @description Called on map's idle event when the map becomes idle after panning
   *              or zooming. Emits changes mapChanged with the updated map state.
   */
  public onIdle(): void {
    const center: google.maps.LatLng = this.map.getCenter();

    this.mapChanged.emit({
      ...this.mapState,
      location: {
        center: { lat: center.lat(), lon: center.lng() },
        northWestCoords: this.mapState.location.northWestCoords,
        southEastCoords: this.mapState.location.southEastCoords,
      },
      zoom: this.map.getZoom(),
    });
  }
  /**
   * @description Called when the map's viewport bounds have changed.
   *              Updates northWest value.
   */
  public onBoundsChange(): void {
    const bounds: google.maps.LatLngBounds = this.map.getBounds();

    this.mapState.location.northWestCoords = {
      lon: bounds.getSouthWest().lng(),
      lat: bounds.getNorthEast().lat(),
    };

    this.mapState.location.southEastCoords = {
      lon: bounds.getNorthEast().lng(),
      lat: bounds.getSouthWest().lat(),
    };
  }

  public acquireInitialLocation(): void {
    this.initialLocationProvider.getInitialLocation((coords, zoom) => {
      this.mapState.location.center = coords;
      if (zoom) {
        this.zoom = zoom;
        this.mapState.zoom = zoom;
      }
      this.flags.isMapInitialized = false;
      safelyDetectChanges(this.changeDetector);
    });
  }

  /**
   * @description Does actions to open or close marker's details box on it depend on
   *              current clicked id and markers list content.
   */
  private processClickedMarkerId(): void {
    if (this.mediator.clickedId) {
      const marker: CustomMarker = this.markers.find((item: CustomMarker) => item.id === this.mediator.clickedId);

      if (marker) {
        this.mapState.location.center = { lat: marker.latitude, lon: marker.longitude };
        safelyDetectChanges(this.changeDetector);

        if (!marker.isAggregated) {
          this.openMarkerPopup(marker);
        }
      }
    }
  }
  /**
   * @description Subscribes to searchItemClicked event to open or close marker's details
   *              box on it.
   */
  private subscribeToMediatorChanges(): void {
    this.subscriptions.push(
      this.mediator.searchItemClicked.subscribe(() => {
        this.processClickedMarkerId();
      }),
      this.mediator.itemClicked.subscribe(() => {
        this.processClickedMarkerId();
      }),
      this.mediator.resetAction.subscribe(() => {
        this.onMapClick();
      }),
    );
  }

  private openMarkerPopup(marker: CustomMarker): void {
    this.popupContent.data = marker.data;
    this.popupContent.tooltips = marker.tooltips;
    this.popupContent.itemsKind = marker.itemKind;
    this.popupContent.itemsCount = marker.itemsCount;
    this.popupContent.kind = marker.kind;
    this.popupContent.ngOnChanges();

    this.popup.position = new google.maps.LatLng(marker.latitude, marker.longitude);
    this.popup.open();
    this.mediator.onMarkerClick(marker.id);
  }

  private updateOptions(): void {
    this.mapOptions = { ...this.mapOptions, minZoom: this.zoomOptions.minZoom, maxZoom: this.zoomOptions.maxZoom };
  }

  private async initMap(): Promise<void> {
    await this.isMapLoaded.then((isLoaded: boolean) => {
      this.mapOptions = DEFAULT_MAP_OPTIONS;
      this.markerOptions = DEFAULT_MARKER_OPTIONS;
      this.circleOptions = DEFAULT_CIRCLE_OPTIONS;
      this.popupOptions = { pixelOffset: new google.maps.Size(0, -20) };

      this.subscribeToMediatorChanges();
      this.subscribeToCityMarkChanges();

      this.acquireInitialLocation();

      return isLoaded;
    });
  }
}
